import {
    Button,
    Grid,
    Typography,
  } from "@mui/material";
  import dayjs from "dayjs";
  import lscache from "lscache";
  import React, { useEffect, useRef, useState } from "react";
  import { FormattedMessage } from "react-intl";
  import { toast } from "react-toastify";
  import { useUser } from "../../contexts/UserContext";
  import { useJobFunctions } from "../../hooks/jobs/useJobFunctions";
  import { useDeleteHandler } from "../../hooks/useDeleteHandler";
  import { useEditHandler } from "../../hooks/useEditHandler";
  import jobService from "../../services/job-service";
  import IJob, {
    JobComponentProps,
  } from "../../types/job-type";
  import BaseCard from "../Handlers/CardHandler";
  import JobForm from "./JobForm";
  
  const JobComponent: React.FC<JobComponentProps> = ({
    canDelete,
  }) => {
    const { user, userId } = useUser();
  
    const initialFormData: IJob = {
      role: "",
      description: "",
      address: "",
      city: "",
      country: "Finland",
      jobType: "full-time",
      company: "",
      salary: "",
      currency: "EUR",
      startingDate: dayjs(),
    };
  
    const [formData, setFormData] = useState<IJob>(initialFormData);
  
    const { couches, isLoading, setCouches, loadJobs } =
      useJobFunctions();
  
    const { handleEditClick, editing, setEditing, showForm, setShowForm } =
      useEditHandler<IJob>(jobService);
  
    const { handleDelete } = useDeleteHandler<IJob>(jobService);
  
    const initialFormDataRef = useRef(initialFormData);
  
    useEffect(() => {
      const cachedFormData: Partial<IJob> = {};
      Object.keys(initialFormDataRef.current).forEach((key) => {
        const jobKey = "job-" + key;
        const cachedValue = lscache.get(jobKey);
        if (cachedValue != null) {
          cachedFormData[key as keyof typeof cachedFormData] = cachedValue; // keyof typeof: "Trust me, this key is one of the valid labels in the cachedFormData box."
        }
      });
  
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...cachedFormData,
      }));
    }, [user, userId]);
  
    const handleEditJob = async (jobData: IJob) => {
      const data = await handleEditClick(jobData);
      if (data) {
        setFormData(data);
      }
    };
    const handleDeleteJob = async (jobData: IJob) => {
      const data = await handleDelete(jobData);
      if (data) {
        const updatedJobs = couches.filter(
          (currentJob) => currentJob.id !== jobData.id
        );
        setCouches(updatedJobs);
        loadJobs();
      }
    };
  
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        const jobContent: IJob = {
          id: formData.id,
          company: formData.company,
          role: formData.role,
          description: formData.description,
          jobType: formData.jobType,
          salary: formData.salary,
          currency: formData.currency,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          creatorId: userId,
          startingDate: formData.startingDate,
        };
        if (editing === true) {
          await jobService.updateJob(
            jobContent.id!,
            jobContent
          );
          toast.success("Job updated successfully!", {
            position: "bottom-right",
          });
        } else {
          await jobService.listJob(jobContent);
          toast.success("Job listed successfully!", {
            position: "bottom-right",
          });
        }
        setEditing(false);
        setFormData(initialFormData);
        Object.keys(initialFormData).forEach((key) => {
          lscache.set(
            "job-" + key,
            initialFormData[key as keyof typeof initialFormData]
          );
        });
        setShowForm(false);
        loadJobs();
      } catch (error) {
        console.error("Error listing job:", error);
        toast.error(
          "Failed to list job. Please check the details and try again.",
          {
            position: "bottom-right",
          }
        );
      }
    };
  
    return (
      <div className="container">
        {isLoading && <div>...Loading</div>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <header>
              <Typography variant="h3">
                <FormattedMessage id="available-jobs" />
              </Typography>
            </header>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              sx={{ mt: 2, mb: 2, width: { xs: "100%", sm: "90%" } }}
              variant="contained"
              onClick={() => setShowForm(!showForm)}
            >
              {showForm === null && (
                <Typography variant="body1">
                  <FormattedMessage id="list-job" />
                </Typography>
              )}
              {showForm !== null && (
                <Typography variant="body1">
                  {showForm ? (
                    <FormattedMessage id="hide" />
                  ) : (
                    <FormattedMessage id="show" />
                  )}
                </Typography>
              )}
            </Button>
          </Grid>
          <Grid item xs={12}>
            {showForm && (
              <JobForm
                editing={editing}
                formData={formData}
                setFormData={setFormData}
                handleSubmit={handleSubmit}
              />
            )}
          </Grid>
          {couches.map((job) => {
            const cardDetailsTop = (
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h6" color="textSecondary">
                    {job.role}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="h6" color="textSecondary">
                    {job.company}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="h6" color="textSecondary">
                    {job.salary} {job.currency}
                  </Typography>
                </Grid>
              </Grid>
            );
            const cardDetailsLeft = (
              <>
                <Typography variant="body1" color="textSecondary">
                  <FormattedMessage id="type" />:{" "}
                  <FormattedMessage id={job.company} />
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  <FormattedMessage id="address" />: {job.address}, {job.city}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {job.country}
                </Typography>
              </>
            );
            return (
              <Grid item xs={12} md={4} key={job.id}>
                <BaseCard
                  creatorId={job.creatorId!}
                  title={job.role}
                  price={job.salary}
                  listingType="job"
                  currency={job.currency}
                  description={job.description}
                  location={`${job.city}, ${job.country}`}
                  topDetails={cardDetailsTop}
                  details={cardDetailsLeft}
                  canDelete={canDelete || userId === job.creatorId}
                  canModify={userId === job.creatorId}
                  onDelete={() => handleDeleteJob(job)}
                  onEdit={() => handleEditJob(job)}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>
    );
  };
  
  export default JobComponent;
  